/**
 * Builder dummy payloads, one file per widget code.
 *
 * Split out of a single 176k-line builderWidgetDataByIdDummy.json, which
 * was slow to even open in an editor. Imports are static rather than
 * require.context so this resolves identically under webpack, Jest and
 * tsc — the builder page is covered by a test, and require.context is not
 * available there.
 *
 * To add a widget: drop `<widget_code>.json` in this folder and add the
 * matching import and map entry below.
 */

import active_cases from "./active_cases.json";
import appeal_abandoned from "./appeal_abandoned.json";
import appeal_abandoned_rate from "./appeal_abandoned_rate.json";
import appeal_approved from "./appeal_approved.json";
import appeal_denied from "./appeal_denied.json";
import appeal_overturn_rate from "./appeal_overturn_rate.json";
import appeal_received from "./appeal_received.json";
import appeal_submitted from "./appeal_submitted.json";
import approval_rate_by_payer from "./approval_rate_by_payer.json";
import areas_territories from "./areas_territories.json";
import avg_tat from "./avg_tat.json";
import avg_time_to_bv from "./avg_time_to_bv.json";
import bv_abandoned from "./bv_abandoned.json";
import bv_abandoned_rate from "./bv_abandoned_rate.json";
import bv_completed from "./bv_completed.json";
import bv_over_time from "./bv_over_time.json";
import bv_started from "./bv_started.json";
import bv_to_pa_submitted_rate from "./bv_to_pa_submitted_rate.json";
import call_duration_histogram from "./call_duration_histogram.json";
import call_sentiment_by_direction from "./call_sentiment_by_direction.json";
import call_sentiment_by_duration from "./call_sentiment_by_duration.json";
import call_sentiment_calls_scored from "./call_sentiment_calls_scored.json";
import call_sentiment_mixed_rate from "./call_sentiment_mixed_rate.json";
import call_sentiment_negative_rate from "./call_sentiment_negative_rate.json";
import call_sentiment_positive_rate from "./call_sentiment_positive_rate.json";
import call_sentiment_scatter from "./call_sentiment_scatter.json";
import call_sentiment_spectrum from "./call_sentiment_spectrum.json";
import call_sentiment_tone_split from "./call_sentiment_tone_split.json";
import call_sentiment_trend from "./call_sentiment_trend.json";
import call_volume_by_sentiment from "./call_volume_by_sentiment.json";
import case_success_bar from "./case_success_bar.json";
import case_volume from "./case_volume.json";
import cases_by_mos from "./cases_by_mos.json";
import cases_by_submission_mode from "./cases_by_submission_mode.json";
import copay_enrollment from "./copay_enrollment.json";
import denial_cohort_reconciliation from "./denial_cohort_reconciliation.json";
import denial_cohort_reconciliation_custom from "./denial_cohort_reconciliation_custom.json";
import denial_rate from "./denial_rate.json";
import denial_reason_capture_rate from "./denial_reason_capture_rate.json";
import denial_reasons_by_payer from "./denial_reasons_by_payer.json";
import denial_to_appeal_pursuit_rate from "./denial_to_appeal_pursuit_rate.json";
import fams_table from "./fams_table.json";
import final_approval_rate from "./final_approval_rate.json";
import first_pass_approval_rate from "./first_pass_approval_rate.json";
import geographical_stats from "./geographical_stats.json";
import icd_data from "./icd_data.json";
import kams_table from "./kams_table.json";
import med_precert_approval_rate from "./med_precert_approval_rate.json";
import med_precert_outcome_mix from "./med_precert_outcome_mix.json";
import med_precert_required_rate from "./med_precert_required_rate.json";
import new_doctors from "./new_doctors.json";
import new_practices from "./new_practices.json";
import outside_24hr from "./outside_24hr.json";
import pa_abandoned from "./pa_abandoned.json";
import pa_abandoned_rate from "./pa_abandoned_rate.json";
import pa_approval_by_insurance from "./pa_approval_by_insurance.json";
import pa_approved from "./pa_approved.json";
import pa_approved_count from "./pa_approved_count.json";
import pa_approved_over_time from "./pa_approved_over_time.json";
import pa_breakdown from "./pa_breakdown.json";
import pa_by_insurance from "./pa_by_insurance.json";
import pa_denial_by_insurance from "./pa_denial_by_insurance.json";
import pa_denials from "./pa_denials.json";
import pa_not_required from "./pa_not_required.json";
import pa_outcome_mix from "./pa_outcome_mix.json";
import pa_required from "./pa_required.json";
import pa_required_rate from "./pa_required_rate.json";
import pa_required_rate_by_payer from "./pa_required_rate_by_payer.json";
import pa_submitted from "./pa_submitted.json";
import pa_tat_by_payer from "./pa_tat_by_payer.json";
import pa_tat_deviation_by_payer from "./pa_tat_deviation_by_payer.json";
import pa_tat_outliers from "./pa_tat_outliers.json";
import patient_dropoff_funnel from "./patient_dropoff_funnel.json";
import patient_success_funnel from "./patient_success_funnel.json";
import patient_success_simple from "./patient_success_simple.json";
import payer_access_tier_cards from "./payer_access_tier_cards.json";
import payer_access_tier_distribution from "./payer_access_tier_distribution.json";
import payer_pa_access_tier_segmentation from "./payer_pa_access_tier_segmentation.json";
import payer_summary_by_volume from "./payer_summary_by_volume.json";
import payers_with_access_barriers from "./payers_with_access_barriers.json";
import precert_denial_root_causes from "./precert_denial_root_causes.json";
import providers_table from "./providers_table.json";
import rbds_table from "./rbds_table.json";
import received_cases_count from "./received_cases_count.json";
import referral_to_bv_complete_rate from "./referral_to_bv_complete_rate.json";
import reverification_volume from "./reverification_volume.json";
import site_performance_quadrant from "./site_performance_quadrant.json";
import site_root_cause from "./site_root_cause.json";
import therapy_covered from "./therapy_covered.json";
import third_party_block_rate_by_payer from "./third_party_block_rate_by_payer.json";
import time_to_pa from "./time_to_pa.json";
import top_denial_reasons from "./top_denial_reasons.json";
import top_payer_types from "./top_payer_types.json";
import top_payors from "./top_payors.json";
import top_prescribers_by_volume from "./top_prescribers_by_volume.json";
import transfers_kpi from "./transfers_kpi.json";
import weekly_performance_trend from "./weekly_performance_trend.json";

// `unknown` values: each file is a raw backend response snapshot whose
// shape varies per chart type, and resolveBuilderWidgetData narrows it.
export const BUILDER_WIDGET_DUMMY: Record<string, unknown> = {
  active_cases,
  appeal_abandoned,
  appeal_abandoned_rate,
  appeal_approved,
  appeal_denied,
  appeal_overturn_rate,
  appeal_received,
  appeal_submitted,
  approval_rate_by_payer,
  areas_territories,
  avg_tat,
  avg_time_to_bv,
  bv_abandoned,
  bv_abandoned_rate,
  bv_completed,
  bv_over_time,
  bv_started,
  bv_to_pa_submitted_rate,
  call_duration_histogram,
  call_sentiment_by_direction,
  call_sentiment_by_duration,
  call_sentiment_calls_scored,
  call_sentiment_mixed_rate,
  call_sentiment_negative_rate,
  call_sentiment_positive_rate,
  call_sentiment_scatter,
  call_sentiment_spectrum,
  call_sentiment_tone_split,
  call_sentiment_trend,
  call_volume_by_sentiment,
  case_success_bar,
  case_volume,
  cases_by_mos,
  cases_by_submission_mode,
  copay_enrollment,
  denial_cohort_reconciliation,
  denial_cohort_reconciliation_custom,
  denial_rate,
  denial_reason_capture_rate,
  denial_reasons_by_payer,
  denial_to_appeal_pursuit_rate,
  fams_table,
  final_approval_rate,
  first_pass_approval_rate,
  geographical_stats,
  icd_data,
  kams_table,
  med_precert_approval_rate,
  med_precert_outcome_mix,
  med_precert_required_rate,
  new_doctors,
  new_practices,
  outside_24hr,
  pa_abandoned,
  pa_abandoned_rate,
  pa_approval_by_insurance,
  pa_approved,
  pa_approved_count,
  pa_approved_over_time,
  pa_breakdown,
  pa_by_insurance,
  pa_denial_by_insurance,
  pa_denials,
  pa_not_required,
  pa_outcome_mix,
  pa_required,
  pa_required_rate,
  pa_required_rate_by_payer,
  pa_submitted,
  pa_tat_by_payer,
  pa_tat_deviation_by_payer,
  pa_tat_outliers,
  patient_dropoff_funnel,
  patient_success_funnel,
  patient_success_simple,
  payer_access_tier_cards,
  payer_access_tier_distribution,
  payer_pa_access_tier_segmentation,
  payer_summary_by_volume,
  payers_with_access_barriers,
  precert_denial_root_causes,
  providers_table,
  rbds_table,
  received_cases_count,
  referral_to_bv_complete_rate,
  reverification_volume,
  site_performance_quadrant,
  site_root_cause,
  therapy_covered,
  third_party_block_rate_by_payer,
  time_to_pa,
  top_denial_reasons,
  top_payer_types,
  top_payors,
  top_prescribers_by_volume,
  transfers_kpi,
  weekly_performance_trend,
};
